/**
 * voice-v8 — Audio-Feedback Helper (Pre.478)
 *
 * Zwei Aufgaben:
 *
 * 1. **PTT-Beeps** — kurze Töne via WebAudio (Oscillator) für Start/Stop/Cancel
 *    der Sprachnachricht. Akustisches Pendant zu vibrate.
 *
 * 2. **iOS-Audio-Unlock** — auf iOS Safari (auch in PWA) darf Audio nur aus
 *    einem User-Gesture-Handler heraus abgespielt werden. Wenn der Bot nach
 *    PTT mit TTS antwortet, läuft `audio.play()` aus einer async-Chain HEX
 *    nach mehreren awaits → iOS verweigert. Lösung: beim ersten Tap einen
 *    AudioContext.resume() + ein muted dummy-play() — danach ist die ganze
 *    Session entsperrt und HTMLAudioElement.play() funktioniert.
 *
 * Best-effort durchgehend: alle Fehler werden geschluckt, weil Audio-
 * Feedback nicht funktionskritisch ist.
 */
import { getCapacitorPlugin, nativesPluginKennt } from "./native-voice.js";
import { entsperrePcm } from "./tts-pcm.js";
import { STILLE_WAV, entsperreDateiSpieler } from "./datei-spieler.js";

let ctx: AudioContext | null = null;
let unlocked = false;

interface AudioContextCtor {
  new (): AudioContext;
}
type WindowWithWebkitAC = Window & { webkitAudioContext?: AudioContextCtor };

/**
 * Der Kontext der kurzen Töne (Quittungen, Morse) — einer für die Seite, angelegt beim ersten Ton.
 * (06.10.2026: exportiert für frontend/lib/morse-ton.ts; KEIN weiterer Kontext — app.ts hat schon drei.)
 */
export function tonKontext(): AudioContext | null {
  return getCtx();
}

/** Läuft der Ton-Kontext gerade (`running`)? Für Töne, die nie verspätet klingen dürfen (Ja/Nein-Bestätigung). */
export function tonKontextLaeuft(): boolean {
  return getCtx()?.state === "running";
}

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === "undefined") return null;
  const w = window as WindowWithWebkitAC;
  const Ctor: AudioContextCtor | undefined =
    window.AudioContext ?? w.webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Aus einem User-Gesture-Handler heraus aufrufen (z.B. pointerdown). Macht
 * den AudioContext + HTMLAudioElement-Pipeline für die restliche Session
 * iOS-freischaltbar.
 */
export function unlockAudio(): void {
  if (unlocked) return;
  unlocked = true;
  // 1) WebAudio Context entsperren
  const c = getCtx();
  if (c?.state === "suspended") {
    void c.resume().catch(() => {
      /* ignore */
    });
  }
  // 1b) Ticket 2919: der PCM-Weg hat seinen eigenen Kontext (nur mit eingeschaltetem Schalter).
  entsperrePcm();
  // Ticket 2919: das eine Element für den Datei-Weg nach Unterbrechungen — einmal in einer Geste gespielt.
  entsperreDateiSpieler();
  // 2) HTMLAudioElement-Pipeline mit muted dummy-play warmlaufen lassen.
  try {
    const dummy = new Audio(STILLE_WAV);
    /*
     * ⚠️ 19.09.2026 (Ticket 2329) — HIER STAND `dummy.muted = true`, UND
     *    DAMIT HAT DIE FREISCHALTUNG NICHTS FREIGESCHALTET.
     *
     * Stummes Abspielen ist im Browser ohnehin immer erlaubt. Es erteilt
     * deshalb KEINE Erlaubnis fuer ungemuteten Ton — genau das ist der
     * Unterschied zwischen „es hat nicht geknallt" und „ich darf jetzt".
     *
     * Am Betrieb gemessen, sieben Tage: Albert (im Browser, `nativ=nein`)
     * 142 Fehlschlaege bei 224 Wiedergaben — 63 %, am 17.09. sogar 57 von
     * 62. Martin in der nativen App: 1302 Wiedergaben, 0 Fehler. Die
     * Autoplay-Regel gilt nur im Browser, und dort hat diese Zeile sie nie
     * beruehrt.
     *
     * Lautlos bleibt es ueber die Lautstaerke — das zaehlt als echte
     * Wiedergabe und erteilt die Erlaubnis.
     */
    dummy.volume = 0;
    const p = dummy.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        try {
          dummy.pause();
        } catch {
          /* ignore */
        }
      }).catch(() => {
        /* iOS spuckt manchmal NotAllowedError trotz user-gesture — ignorieren */
      });
    }
  } catch {
    /* ignore */
  }
}

/** So lange darf die stille Probe brauchen. */
export const TON_PROBE_FRIST_MS = 1500;

/**
 * Darf JETZT, ohne frischen Tipp, Ton kommen? (27.09.2026, Ticket 2698)
 *
 * „Essenzspiegel vorlesen“ startet den Player erst, wenn Vesperas eigener
 * Satz verklungen ist — Sekunden nach dem letzten Tipp. Im Browser ist der
 * Ton dann womöglich gesperrt (Ticket 2329: bei Albert 63 % Fehlschläge,
 * in der App 0). Ein gesperrter Player meldet „Vorlesen gerade nicht
 * möglich“ — ein Fehler, den es nicht gibt. Darum vorher fragen: dieselbe
 * stille Datei wie `unlockAudio`, lautlos über die Lautstärke (das zählt
 * als echte Wiedergabe). Nein heißt: `NotAllowedError` oder keine Antwort
 * binnen der Frist — jeder andere Fehler sagt nichts über die Sperre.
 */
export async function tonOhneTippMoeglich(fristMs = TON_PROBE_FRIST_MS): Promise<boolean> {
  let uhr = 0;
  try {
    const probe = new Audio(STILLE_WAV);
    probe.volume = 0;
    // Bleibt die Antwort aus, lieber anbieten als auf ewig warten.
    const frist = new Promise<boolean>((fertig) => {
      uhr = window.setTimeout(() => fertig(false), fristMs);
    });
    const erlaubt = await Promise.race([
      Promise.resolve(probe.play()).then(() => {
        try {
          probe.pause();
        } catch {
          /* ignore */
        }
        return true;
      }),
      frist,
    ]);
    return erlaubt;
  } catch (err) {
    return (err as { name?: string } | null)?.name !== "NotAllowedError";
  } finally {
    window.clearTimeout(uhr);
  }
}


/**
 * Beim ERSTEN Antippen der Sitzung freischalten — egal wo. (19.09.2026)
 *
 * ⚠️ `unlockAudio()` wurde bisher an drei Stellen gerufen: Anruf, PTT-Knopf
 *    und die Tiefen Fragen. Wer TIPPT und sich die Antwort vorlesen laesst,
 *    kam an keiner davon vorbei — und genau dort schlug der Fehler zu.
 *
 * Statt jeden Knopf einzeln zu verdrahten (und den naechsten zu vergessen):
 * einmal auf die erste Geste des Dokuments hoeren. `once` raeumt sich selbst
 * ab, `capture` kommt vor jedem Handler, der die Geste verschluckt.
 *
 * ⚠️ `pointerdown` UND `keydown`: wer auf der Tastatur schreibt und mit der
 *    Eingabetaste sendet, erzeugt kein Zeigerereignis.
 */
export function freischaltenBeiErsterGeste(): void {
  if (typeof document === "undefined") return;
  const opts = { once: true, capture: true } as const;
  for (const ev of ["pointerdown", "keydown", "touchstart"]) {
    try {
      document.addEventListener(ev, () => unlockAudio(), opts);
    } catch {
      /* ein fehlender Ereignistyp darf den Start nie aufhalten */
    }
  }
}

/** Ein geplanter Sinuston. Zeiten in Sekunden auf der Uhr des Kontexts. */
interface TonPlan {
  freq: number;
  /** Beginn auf `currentTime` des Kontexts. */
  beginn: number;
  dauer: number;
  lautstaerke: number;
  /**
   * `abklingen`: kurzer Anstieg (5 ms), dann exponentiell aus — die Quittungen.
   * `flach`: Anstieg und Abfall je `anstieg`, dazwischen gleich laut — Morse:
   *          ein Punkt muss so lang KLINGEN, wie er dauert, sonst hört man ihn kürzer.
   */
  form?: "abklingen" | "flach";
  /** Anstieg und Abfall bei `flach` (Sekunden, Vorgabe 5 ms) — gegen das Knacken. */
  anstieg?: number;
  /** Wohin der Ton geht (Vorgabe: `destination`). */
  ziel?: AudioNode;
}

/**
 * EIN Ton mit Hüllkurve (kein Knacken). Alle Töne der Seite laufen hier durch —
 * die Quittungen (`playBeep`) und Morse (morse-ton.ts). Nimmt jeden Kontext,
 * auch einen `OfflineAudioContext` (so prüft der Test die Zeiten am Signal).
 * Gibt den Oszillator zurück (zum vorzeitigen Stoppen).
 */
export function planeTon(c: BaseAudioContext, t: TonPlan): OscillatorNode {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = "sine";
  osc.frequency.value = t.freq;
  gain.gain.setValueAtTime(0, t.beginn);
  if (t.form === "flach") {
    const a = Math.min(t.anstieg ?? 0.005, t.dauer / 2);
    gain.gain.linearRampToValueAtTime(t.lautstaerke, t.beginn + a);
    gain.gain.setValueAtTime(t.lautstaerke, t.beginn + t.dauer - a);
    gain.gain.linearRampToValueAtTime(0, t.beginn + t.dauer);
  } else {
    gain.gain.linearRampToValueAtTime(t.lautstaerke, t.beginn + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, t.beginn + t.dauer);
  }
  osc.connect(gain).connect(t.ziel ?? c.destination);
  osc.start(t.beginn);
  osc.stop(t.beginn + t.dauer + (t.form === "flach" ? 0.005 : 0.01));
  return osc;
}

/** Flanke des Bereit-Tons auf und ab (Sekunden): 15 ms, zwischen 10 und 20. */
export const BEREIT_FLANKE_S = 0.015;

type BeepKind = "start" | "stop" | "cancel" | "achtung" | "gesendet" | "rueckfrage" | "aufstieg" | "bereit" | "ja" | "nein";

/**
 * Kurzer Sinus-Ton via Oscillator. Sehr leise (0.06 gain), kein Click —
 * Envelope mit attack + release. Nicht-blocking.
 */
export function playBeep(kind: BeepKind): void {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") {
    void c.resume().catch(() => {
      /* ignore */
    });
  }
  const now = c.currentTime;
  const cfg = (() => {
    switch (kind) {
      case "start":
        return { freq: 880, dur: 0.08, gain: 0.08 };
      case "stop":
        return { freq: 660, dur: 0.1, gain: 0.08 };
      case "cancel":
        // zwei absteigende Töne — eindeutig "abgebrochen"
        return { freq: 440, dur: 0.08, gain: 0.07 };
      /*
       * ⚠️⚠️ 18.09.2026 — DER EINZIGE TON, DER JEMANDEN ERREICHEN MUSS.
       *
       * Die drei anderen sind Quittungen: Wer sie hört, hat gerade selbst
       * getippt und schaut hin. Dieser hier gilt jemandem, der SPRICHT und
       * das Telefon am Ohr hat.
       *
       * Albert sprach am 16.09. um 07:23 los. Um 07:38:24 war die Aufnahme
       * bei exakt 15:00,02 am Limit und wurde abgeschickt. Danach redete er
       * SECHS MINUTEN weiter: *„Jetzt habe ich eine halbe Stunde gesprochen
       * und gemerkt, dass das Handy gar nicht aufnimmt."* Die einzige
       * Meldung war sichtbar — und Vibration gibt es im iPhone-Browser
       * nicht.
       *
       * Deshalb dreimal so laut wie die Quittungen (0,24 statt 0,08), tiefer
       * (dringt besser durch eine Stimme) und doppelt. Eine Einblendung
       * erreicht niemanden, der nicht hinsieht.
       *
       * ⚠️ Trotzdem kein Alarm: 0,24 ist deutlich, nicht erschreckend. Wer
       *    hier aufdreht, baut etwas, das Martin abschaltet — und dann hört
       *    wieder niemand etwas.
       */
      case "achtung":
        return { freq: 520, dur: 0.16, gain: 0.24 };
      /*
       * 26.09.2026 — „weg ist sie", für jemanden, der NICHT hinschaut.
       *
       * Martin: *„dass wenn automatisch versendet wird, nach zwei Sekunden
       * oder drei Sekunden, dass dann auch ein kleines Sendegeräusch kommt …
       * wenn ich zum Beispiel jetzt das Handy nicht ansehe."*
       *
       * Zwei Töne AUFWÄRTS (Quinte) — das Gegenstück zu „cancel", das
       * abwärts geht. Es bestätigt, es warnt nicht.
       *
       * ⚠️ Lauter als die Quittungen (0,14 statt 0,08), leiser als „achtung"
       *    (0,24): Die anderen hört, wer gerade getippt hat und hinschaut —
       *    diesen hier soll hören, wer NICHT hinschaut. Martin wünschte sich
       *    „so wie beim Freischalten" (nativer Systemton), und der ist gut
       *    hörbar. Erschrecken soll er trotzdem nicht.
       */
      case "gesendet":
        return { freq: 880, dur: 0.07, gain: 0.14 };
      /*
       * 27.09.2026 (Ticket 2660) — „da fragt dich jemand", im Auto.
       *
       * Martin: *„Ton-Signal: hörbarer Hinweis, dass gerade eine
       * Rückfrage/Meldung eingetroffen ist — damit er überhaupt merkt,
       * dass etwas ansteht."*
       *
       * Zwei weiche Töne aufwärts wie eine fragende Stimme (Quarte), tiefer
       * und langsamer als „gesendet", damit beide nicht verwechselt werden.
       * Gleich laut wie „gesendet" (0,14): auch der soll jemanden erreichen,
       * der NICHT hinschaut. ⚠️ Kein Doppelton wie „achtung" — das hier ist
       * eine Einladung, keine Warnung.
       */
      case "rueckfrage":
        return { freq: 587, dur: 0.22, gain: 0.14 };
      /*
       * 16.10.2026 — eine Morse-Stufe geschafft (Martin: eine kleine Feier). Vier Töne aufwärts (Dreiklang bis zur
       * Oktave, C–E–G–C), der letzte länger. So leise wie die Quittungen (0,08): wer übt, hat das Telefon in der Hand
       * oder AirPods im Ohr — es soll freuen, nicht erschrecken. Nur nach dem Ende einer Runde (morse-view.ts).
       */
      case "aufstieg":
        return { freq: 523, dur: 0.12, gain: 0.08 };
      /*
       * 19.10.2026 — „das Mikro ist da, sprich“ (nach dem ersten wirklichen Aufnahmebeginn, ptt-modal.ts). Leiser als die
       * Quittungen (0,05 statt 0,08), 70 ms, flach mit 15-ms-Flanken auf und ab (kein Knacken, kein Nachklingen in die
       * Aufnahme). Eigene Art, damit „start“ unverändert bleibt.
       */
      case "bereit":
        return { freq: 880, dur: 0.07, gain: 0.05 };
      /*
       * 20.10.2026 — „dein Tipp auf Ja/Nein ist angekommen“ (lib/frage-knoepfe.ts). Zwei sehr kurze Töne (je 55 ms, zusammen
       * ca. 115 ms): Ja aufwärts (E5→A5), Nein abwärts (587→440 Hz, D5→A4, nicht strafend). Leise (0,04), flache Flanken wie „bereit“.
       */
      case "ja":
        return { freq: 659, dur: 0.055, gain: 0.04 };
      case "nein":
        return { freq: 587, dur: 0.055, gain: 0.04 };
    }
  })();
  /** Ein Ton mit Hüllkurve (kein Knacken). Alle Arten nutzen denselben (`planeTon`). */
  const ton = (freq: number, t: number): void => {
    planeTon(c, { freq, beginn: t, dauer: cfg.dur, lautstaerke: cfg.gain });
  };
  try {
    if (kind === "bereit") {
      planeTon(c, { freq: cfg.freq, beginn: now, dauer: cfg.dur, lautstaerke: cfg.gain, form: "flach", anstieg: BEREIT_FLANKE_S });
      return;
    }
    if (kind === "ja" || kind === "nein") {
      const zweiter = kind === "ja" ? 880 : 440;
      const flach = (freq: number, beginn: number): void => {
        planeTon(c, { freq, beginn, dauer: cfg.dur, lautstaerke: cfg.gain, form: "flach", anstieg: BEREIT_FLANKE_S });
      };
      flach(cfg.freq, now);
      flach(zweiter, now + 0.06);
      return;
    }
    ton(cfg.freq, now);
    if (kind === "achtung") {
      // Der zweite Schlag macht den Unterschied zwischen „da war was" und
      // „das galt mir". Gleiche Höhe, damit es nicht nach Fehler klingt.
      ton(cfg.freq, now + 0.26);
    }
    if (kind === "cancel") {
      // Zweiter, tieferer Ton kurz danach
      ton(330, now + 0.09);
    }
    if (kind === "gesendet") {
      // Zweiter, höherer Ton direkt danach — aufwärts heißt „abgeschickt".
      ton(1320, now + 0.075);
    }
    if (kind === "rueckfrage") {
      // Der zweite eine Quarte höher, mit Luft dazwischen — fragend, nicht eilig.
      ton(784, now + 0.2);
    }
    if (kind === "aufstieg") {
      ton(659, now + 0.12);
      ton(784, now + 0.24);
      planeTon(c, { freq: 1047, beginn: now + 0.36, dauer: 0.36, lautstaerke: cfg.gain });
    }
  } catch {
    /* ignore */
  }
}

/**
 * Ist dieser Zug ohne Knopfdruck abgegangen? (26.09.2026)
 *
 * Die Anlässe sind dieselben Wörter, die `pttFinishAndSend` ins Protokoll
 * schreibt (`[ptt-ende] senden anlass=…`) — eine Quelle, kein zweites Feld.
 *
 * - `selbst-stille` / `selbst-wort`: „Absenden nach Stille" bzw. das
 *   Auslösewort im Sprechtasten-Fenster (ptt-modal.ts, jetzt-senden.ts).
 * - `freihand-pause`: Freihand hat das Ende des Satzes erkannt.
 * - `wachwort-satz`: der Satz nach „Hey Vespera" in einem Atemzug.
 *
 * ⚠️ `hoechstdauer` gehört NICHT dazu, obwohl es auch von selbst abgeht:
 *    dort klingt schon der lautere Doppelton „achtung" (app.ts, onAutoStop).
 *    Zwei Töne für einen Zug wären einer zu viel.
 * ⚠️ Alles andere — Loslassen, Senden im Fenster, Umschalter — war ein
 *    Druck. Wer drückt, spürt es ohnehin (`sendeHaptik` an der Stelle).
 */
export function istSelbstGesendet(anlass: string): boolean {
  return (
    anlass.startsWith("selbst-") ||
    anlass === "freihand-pause" ||
    anlass === "wachwort-satz"
  );
}

/**
 * Darf der Server dieses Diktat per „Verwerfen"/„Stopp" am Ende SEINER
 * Erkennung verwerfen? (29.09.2026, Ticket 2780, Nachbesserung)
 *
 * Nur nach `selbst-stille`: ein Diktat im Sprechtasten-Fenster, das nach
 * Stille abging — der Fall aus dem Ticket. Ein Druck hat entschieden.
 *
 * ⚠️ NICHT `freihand-pause` und `wachwort-satz`, obwohl auch sie von selbst
 *    abgehen: Dort spricht man mit Vespera, man diktiert nicht. Ein
 *    „Vespera … Stopp" geht als kurzer Satz ans Modell
 *    (server/kurz-phantom.ts lässt „stopp" ausdrücklich durch) — so bleibt es.
 * ⚠️ NICHT `selbst-wort`: da hat schon ein Endwort die Aufnahme beendet.
 */
export function darfAmServerVerwerfen(anlass: string): boolean {
  return anlass === "selbst-stille";
}

/**
 * Unter dieser Größe ist eine Aufnahme leer — dieselbe Schwelle wie der
 * Server (voice-command.ts, MIN_AUDIO_BYTES). `pttSendBlob` schickt so eine
 * gar nicht erst ab, und „gesendet" klingt für sie nie.
 */
export const LEERE_AUFNAHME_BYTES = 512;

/**
 * Die Quittung „gesendet" für EINEN Versand — oder keine. (26.09.2026)
 *
 * Gibt den Rückruf zurück, den `streamPtt` ruft, sobald der Server die
 * Aufnahme angenommen hat (`onAngenommen`); bei einem Druck, bei der
 * Höchstdauer oder einer leeren Aufnahme `undefined`.
 *
 * ⚠️ NICHT beim Stopp der Aufnahme klingen lassen. Bis pre.3318 klang es
 *    dort — vor der Leer-Prüfung und vor dem Hochladen. Bei leerer Aufnahme
 *    oder ohne Netz hörte man „gesendet", und es kam nichts an.
 * ⚠️ Höchstens EINMAL, auch wenn der Rückruf zweimal gerufen wird.
 * ⚠️ Kennt die App `sendeQuittung` (ab Build 86), spielt SIE Ton und
 *    Vibration — auch bei verdeckter Seite und offenem Mikrofon. Dann
 *    nichts zusätzlich aus der Seite; scheitert der Aufruf, der Web-Ton.
 * ⚠️ `nimmtAuf`: Das Hochladen darf bis zu 3×180 s dauern, der Wachhund
 *    gibt die Sprechtaste nach 90 s frei. Kommt die Annahme eines ALTEN
 *    Zugs, während schon die nächste Aufnahme läuft, landete der Ton in
 *    ihr. Dann nur die Vibration.
 */
export function quittungFuerVersand(
  anlass: string,
  bytes: number,
  nimmtAuf: () => boolean = () => false,
): (() => void) | undefined {
  if (bytes < LEERE_AUFNAHME_BYTES || !istSelbstGesendet(anlass)) return undefined;
  let erklungen = false;
  const webQuittung = (): void => {
    playBeep("gesendet");
    sendeHaptik();
  };
  return () => {
    if (erklungen) return;
    erklungen = true;
    if (nimmtAuf()) {
      sendeHaptik();
      return;
    }
    if (!nativeSendeQuittung(webQuittung)) webQuittung();
  };
}

interface QuittungPlugin {
  sendeQuittung(): Promise<void>;
}

/**
 * Die Quittung aus der App: Systemton 1114 und Erfolgs-Vibration
 * (PawbotRecorderPlugin.swift, ab Build 86). `true` heißt, die App hat
 * übernommen; ältere Fassungen kennen den Namen nicht (`PluginHeaders`).
 * Scheitert der Aufruf erst später, ruft er `sonst` — genau einmal.
 */
function nativeSendeQuittung(sonst: () => void): boolean {
  if (!nativesPluginKennt("PawbotRecorder", "sendeQuittung")) return false;
  const p = getCapacitorPlugin<QuittungPlugin>("PawbotRecorder");
  if (typeof p?.sendeQuittung !== "function") return false;
  try {
    void Promise.resolve(p.sendeQuittung()).catch(() => sonst());
    return true;
  } catch {
    return false;
  }
}

/**
 * Die Vibration des Sende-Knopfs — kräftig, dann leicht. EINE Folge für
 * jeden Weg, der „abgeschickt" fühlen lässt: den Knopf im Fenster, das
 * Loslassen der Sprechtaste und die Quittung ohne Knopfdruck.
 */
export function sendeHaptik(): void {
  hapticTap(20);
  hapticTap(10);
}

/** Haptic feedback (Android + neuere iOS). Best-effort. */
export function hapticTap(ms = 15): void {
  try {
    // 2026-07-05 (Martin): echtes Haptik-Feedback am iPhone ueber das native
    // Capacitor-Haptics-Plugin (navigator.vibrate ignoriert iOS komplett).
    const cap = (
      window as unknown as {
        Capacitor?: {
          Plugins?: {
            Haptics?: { impact?: (o: { style: string }) => void };
          };
        };
      }
    ).Capacitor;
    const H = cap?.Plugins?.Haptics;
    if (H?.impact) {
      const style = ms >= 35 ? "HEAVY" : ms >= 18 ? "MEDIUM" : "LIGHT";
      H.impact({ style });
      return;
    }
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(ms);
    }
  } catch {
    /* ignore */
  }
}
